import React, { FC, useMemo } from "react";

import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable, filtersText } from "../ask/reportAsk.utils";
import { completedStats, DATE_PRESETS, presetRange } from "../reports.utils";
import { scopeUserIds, useReportsAccess } from "../reportsAccess";
import { useViewExtra } from "../saved/SavedViewContext";
import {
  DateMenu,
  PeopleClientTypeFilters,
  useReportFilters,
} from "../ui/ReportFilters";
import {
  Card,
  ColumnChart,
  Empty,
  Kpi,
  KpiGrid,
  Legend,
  LoadError,
  Loading,
  MiniBar,
  Note,
  NUM,
  ReportPage,
  Segmented,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";
import { useAllWorks } from "../useReportData";

type GroupBy = "person" | "workType";

const GROUP_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: "person", label: "By team member" },
  { value: "workType", label: "By work type" },
];

export const CompletedReport: FC = () => {
  const access = useReportsAccess();
  const state = useReportFilters("thisYear");
  const { filters } = state;
  const [by, setBy] = useViewExtra<GroupBy>(
    "groupBy",
    "person",
    GROUP_OPTIONS,
    "",
  );
  const works = useAllWorks({
    hideGreenStatus: false,
    workStatuses: ["Completed"],
    userIds: scopeUserIds(access, filters.userIds),
    customerIds: filters.clientIds,
    worktypeIds: filters.workTypeIds,
  });
  const range = useMemo(() => presetRange(filters.preset), [filters.preset]);
  const stats = useMemo(
    () => completedStats(works.data, range),
    [works.data, range],
  );
  const rows = by === "person" ? stats.byPerson : stats.byWorkType;
  const days = (d: number | null) =>
    d === null ? "—" : `${Math.round(d * 10) / 10}`;

  const onExport = () =>
    exportDataToExcel(
      rows.map((r) => ({
        [by === "person" ? "Team member" : "Work type"]: r.name,
        Completed: r.completed,
        Late: r.late,
        "On time %": r.onTimePct,
        "Average days to finish":
          r.avgDays === null ? "" : Math.round(r.avgDays * 10) / 10,
      })),
      "Completed work",
    );

  const v = (x: React.ReactNode) => (works.isLoading ? "…" : x);

  const firstCol = by === "person" ? "Team member" : "Work type";
  const tableName = `Completed by ${firstCol.toLowerCase()}`;
  const shownRows = useFocusRows(tableName, rows, (r) => r.id || r.name);

  const ask = useMemo(
    () => ({
      title: "Completed work",
      path: "/reports/completed",
      description:
        "How much work was completed, how much was finished by its due date, and how long it took from start date to completion.",
      filters: filtersText([
        `Completed: ${
          DATE_PRESETS.find((p) => p.value === filters.preset)?.label
        }`,
        `Breakdown by ${firstCol.toLowerCase()}`,
        !access.firmWide && "Only your own work",
      ]),
      kpis: [
        { label: "Completed", value: String(stats.total) },
        { label: "On time", value: `${stats.onTimePct}%` },
        {
          label: "Average time to finish",
          value: stats.avgDays === null ? "—" : `${days(stats.avgDays)} days`,
        },
        { label: "Completed late", value: String(stats.late) },
      ],
      tables: [
        askTable(
          "Completed per month",
          stats.months,
          [
            {
              key: "month",
              label: "Month",
              kind: "text",
              value: (m) => m.label,
            },
            {
              key: "completed",
              label: "Completed",
              kind: "number",
              value: (m) => m.total,
            },
            {
              key: "onTime",
              label: "On time",
              kind: "number",
              value: (m) => m.onTime,
            },
            {
              key: "late",
              label: "Late",
              kind: "number",
              value: (m) => m.late,
            },
          ],
          (m) => m.key,
        ),
        askTable(
          tableName,
          rows,
          [
            {
              key: "name",
              label: firstCol,
              kind: "text",
              value: (r) => r.name,
            },
            {
              key: "completed",
              label: "Completed",
              kind: "number",
              value: (r) => r.completed,
            },
            {
              key: "late",
              label: "Late",
              kind: "number",
              value: (r) => r.late,
            },
            {
              key: "onTime",
              label: "On time",
              kind: "percent",
              value: (r) => r.onTimePct,
            },
            {
              key: "avgDays",
              label: "Average days to finish",
              kind: "number",
              value: (r) =>
                r.avgDays === null ? null : Math.round(r.avgDays * 10) / 10,
            },
          ],
          (r) => r.id || r.name,
        ),
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stats, rows, firstCol, filters.preset, access.firmWide],
  );

  return (
    <ReportPage
      title={"Completed work"}
      description={
        "How much work was completed, how much was finished by its due date, and how long it took from start date to completion."
      }
      onExport={rows.length ? onExport : undefined}
      ask={works.isLoading ? undefined : ask}
      askSuggestions={[
        "Who completed the most work?",
        "Which work type is most often late?",
        "Which month had the most late work?",
      ]}
      filters={
        <>
          <DateMenu
            value={filters.preset}
            onChange={(preset) => state.setFilters({ preset })}
            label={"Completed"}
          />
          <PeopleClientTypeFilters state={state} showPeople={access.firmWide} />
        </>
      }>
      {works.isError && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Completed"}
          value={v(stats.total)}
          note={"In this period"}
        />
        <Kpi
          label={"On time"}
          value={v(`${stats.onTimePct}%`)}
          note={"Completed by the due date"}
          tone={"green"}
        />
        <Kpi
          label={"Average time to finish"}
          value={v(
            stats.avgDays === null ? "—" : `${days(stats.avgDays)} days`,
          )}
          note={"From start date to completed"}
        />
        <Kpi
          label={"Completed late"}
          value={v(stats.late)}
          note={"After the due date"}
          tone={"red"}
        />
      </KpiGrid>

      <Card
        title={"Completed per month"}
        right={
          <Legend
            items={[
              { label: "On time", color: "#6B4EFF" },
              { label: "Late", color: "#F2A39A" },
            ]}
          />
        }>
        {works.isLoading ? (
          <Loading />
        ) : (
          <ColumnChart
            items={stats.months.map((m) => ({
              key: m.key,
              label: m.label,
              value: m.total,
              low: m.late,
            }))}
          />
        )}
      </Card>

      <Card
        title={"Breakdown"}
        right={
          <Segmented
            label={"Group by"}
            value={by}
            onChange={setBy}
            options={[
              { value: "person", label: "By team member" },
              { value: "workType", label: "By work type" },
            ]}
          />
        }>
        {works.isLoading ? (
          <Loading />
        ) : works.isError ? null : !rows.length ? (
          <Empty text={"No work was completed in this period."} />
        ) : (
          <TableBox>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  {by === "person" ? "Team member" : "Work type"}
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Completed
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Late
                </th>
                <th scope={"col"} className={TH}>
                  On time
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Average days to finish
                </th>
              </tr>
            </thead>
            <tbody>
              {shownRows.map((r) => (
                <tr key={r.id || r.name}>
                  <td className={`${TD} font-bold`}>{r.name}</td>
                  <td className={`${TD} ${NUM}`}>{r.completed}</td>
                  <td className={`${TD} ${NUM}`}>{r.late}</td>
                  <td className={TD}>
                    <span className={"inline-block w-10 font-bold"}>
                      {r.onTimePct}%
                    </span>
                    <MiniBar
                      pct={r.onTimePct}
                      color={r.onTimePct >= 85 ? "#16A34A" : "#E08A1E"}
                    />
                  </td>
                  <td className={`${TD} ${NUM}`}>{days(r.avgDays)}</td>
                </tr>
              ))}
            </tbody>
          </TableBox>
        )}
      </Card>

      <Note>
        The completion date is when the work&apos;s status last changed. If
        someone changes the status of completed work again later, that work
        moves to the later date.
        {stats.noDate > 0 &&
          ` ${stats.noDate} completed work ${
            stats.noDate === 1 ? "item has" : "items have"
          } no recorded completion date (usually older work), so ${
            stats.noDate === 1 ? "it is" : "they are"
          } not counted.`}
      </Note>
    </ReportPage>
  );
};
